import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonSpinner, IonIcon } from "@ionic/react";
import { businessOutline, checkmarkCircle, ellipseOutline } from "ionicons/icons";
import { ShareableOrg } from "../../../services/orgSharing";
import { useOrgSharingContext } from "./OrgSharingContext";
import {
  filterOrgTree,
  filterOrgs,
  flattenLocations,
  orgDisplayName,
  shareStatusOf,
} from "./useOrgSharing";
import UnshareOrgConfirm from "./UnshareOrgConfirm";
import OrgRow from "./OrgRow";
import OrgAccordionHeader from "./OrgAccordionHeader";
import "./ShareOrgs.css";

// Org-sharing SECTION (ACDPA-186): an ACCORDION of care organizations (no search box of its own).
// The search text is supplied by ONE input owned by the surrounding popup, so each popup has a
// single search that works across its sections.
//
// A patient may share ONLY with a LOCATION (child org), never a parent/top-level org. So parents
// render as expand-only HEADERS; tapping one reveals its locations, each with the Share action.
// Under single_org (the default) the kernel marks every other location non-shareable once one is
// shared, so the UI just renders each location's `shareable`/`is_shared` flags — no client-side
// counting.
//
// Two presentations:
//  • Default (explicit "Share with an organization" popup): a card with an optional header + the
//    accordion, first few parents then "Show all".
//  • autocomplete (regular invite): blends into the invite form. While the user types, the header
//    is hidden and matching LOCATIONS render as a compact scrollable DROPDOWN right under the input,
//    so results stay above the keyboard instead of being pushed underneath it.
//
// State is the shared OrgSharingContext, so a share here updates "Who can see my data". Toast is
// rendered by ShareOrgsSection.
//
// F-468 (Dev UAT) changes to the card view:
//  • nothing is listed until the patient types in the search field;
//  • each location shows its whole address (OrgRow);
//  • shareable locations are SELECTED (a check control) rather than shared one by one, and a
//    sticky "Share" button shares every selected location at once (useOrgSharing.shareMany calls
//    the kernel once per location). Selections survive a new search, so a patient can pick
//    locations of different organizations. When the kernel's policy allows only one location
//    (`can_share_multiple` false, single_org), picking another replaces the pick.
//
// D-303 (owner ruling on D68 part 3, superseding the F-468 note above): a patient shares with ONE
// organisation location at a time, full stop — the picker stays single-select even for a
// multi_org profile where the kernel reports `can_share_multiple: true`. See ALLOW_KERNEL_MULTI_SELECT
// below.
//
// F-470 (Dev UAT): a successful share should close the outer "Share with an organization" sheet.
// The caller (SharingModal, focus="org") supplies `onShared` for that; the autocomplete usage
// inside the invite form does not, so that flow is unaffected.
//
// F-524 (UAT, URS_6.8): no second confirmation. Pressing Share (the sticky card button, or a
// location's Share in the autocomplete dropdown) sends the request directly; the "Confirm
// sharing / I Agree" popup (ShareOrgConfirm) is gone. The success feedback is unchanged: the
// shared toast from useOrgSharing plus onShared closing the host sheet.
type Props = {
  /** Section label; omit when the surrounding view already has a heading. */
  title?: string;
  subtitle?: string;
  /** Filter text, supplied by the popup's single search input. */
  search: string;
  /** Render nothing when the patient is blocked from org sharing. */
  hideWhenBlocked?: boolean;
  /** Blend into the invite form and behave like an autocomplete dropdown while searching. */
  autocomplete?: boolean;
  /**
   * Called once a share completes successfully (F-470) — e.g. so the host can close the outer
   * sheet instead of leaving the patient looking at an empty card behind the dismissed confirm
   * popup. Not called on failure, and not required by callers that have no outer sheet to close.
   */
  onShared?: () => void;
};

const INITIAL_PARENTS = 5;

// D-303 (owner ruling, 2026-09-24, on D68 part 3): keep the share picker single-select even when
// the kernel's sharing policy is multi_org and reports `can_share_multiple: true`. Flip this to
// `true` (or delete it and use `!!policy?.can_share_multiple` directly) if a future owner ruling
// re-enables multi-location selection.
const ALLOW_KERNEL_MULTI_SELECT = false;

const OrgShareCard: React.FC<Props> = ({
  title,
  subtitle,
  search,
  hideWhenBlocked,
  autocomplete,
  onShared,
}) => {
  const { orgs, policy, loading, busyOrgId, share, shareMany, unshare, blocked, nameById } =
    useOrgSharingContext();
  // Card view: the locations the patient has selected to share with, in the order picked.
  const [selected, setSelected] = useState<ShareableOrg[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // F-524: Share now sends straight away, so a quick double press must not send twice before the
  // `submitting` re-render disables the button (the confirm popup used to absorb that).
  const inFlight = useRef(false);
  // The PENDING location whose "Sent request" was tapped (opens the "Cancel request?" popup).
  const [unshareOrg, setUnshareOrg] = useState<ShareableOrg | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Which parent orgs are open in the accordion (by org_id).
  const [openParents, setOpenParents] = useState<Set<number>>(new Set());

  // Collapse the "Show all" paging back to the first few whenever the filter changes.
  useEffect(() => {
    setExpanded(false);
  }, [search]);

  if (blocked && hideWhenBlocked) return null;

  // D-303: single-select regardless of what the kernel's policy reports.
  const canSelectMany = ALLOW_KERNEL_MULTI_SELECT && !!policy?.can_share_multiple;
  const isSelected = (org: ShareableOrg) => selected.some((o) => o.org_id === org.org_id);
  const toggleSelected = (org: ShareableOrg) =>
    setSelected((prev) => {
      if (prev.some((o) => o.org_id === org.org_id)) {
        return prev.filter((o) => o.org_id !== org.org_id);
      }
      return canSelectMany ? [...prev, org] : [org];
    });

  const doShare = async (targets: ShareableOrg[]) => {
    if (inFlight.current || targets.length === 0) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      if (autocomplete) {
        // The invite form's dropdown shares one location per tap.
        const ok = await share(targets[0]);
        // F-470: tell the host a share went through so it can close its outer sheet too.
        if (ok) onShared?.();
      } else {
        const result = await shareMany(targets);
        setSelected([]);
        // F-470: only a share with no failures counts as "successful" for closing the sheet.
        if (result.shared.length > 0 && result.failed.length === 0) onShared?.();
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const doUnshare = async (org: ShareableOrg) => {
    await unshare(org);
    setUnshareOrg(null);
  };

  const parentNameOf = (org: ShareableOrg) =>
    org.parent_id != null ? nameById.get(org.parent_id) : undefined;

  const searching = search.trim().length > 0;
  // Autocomplete shows matching LOCATIONS as a flat scrollable dropdown; the card view shows the
  // parent→locations accordion and pages parents with "Show all".
  const showAsDropdown = !!autocomplete && searching;
  // Hide the header/subtitle while actively searching in autocomplete mode (dropdown feel).
  const showHeader = !(autocomplete && searching);

  // A location's trailing control: a pending "Sent request" tag, an already-shared tag, a Share
  // button, or a disabled "Not available" (parent orgs never reach here — they render as headers).
  const trailingFor = (org: ShareableOrg) =>
    shareStatusOf(org) === "pending" ? (
      // Request awaiting the org's approval — tap to withdraw ("Cancel request?", the same copy as
      // the pending row's action on "Who can see my data"). Withdrawing a request is not leaving.
      <button
        type="button"
        className="care-org-shared-tag care-org-shared-tag-btn"
        style={{ color: "#B45309", borderColor: "#F59E0B" }}
        onClick={() => setUnshareOrg(org)}
        disabled={busyOrgId === org.org_id}
        aria-label={`Cancel your request to ${orgDisplayName(org, nameById)}`}
      >
        {busyOrgId === org.org_id ? <IonSpinner name="dots" /> : "Sent request"}
      </button>
    ) : org.is_shared ? (
      // Already sharing with this location. ⛔ F-492 (UAT, URS_6.8): a LABEL, not a button. It
      // used to open "Revoke Access" and run the share-scoped DELETE — a second exit beside the X
      // on "Who can see my data", which is now the patient's one way to leave.
      <span className="care-org-shared-tag">Shared</span>
    ) : org.shareable && !autocomplete ? (
      // Card view: select now, then share the selection with the sticky Share button (F-524: no
      // confirm popup in between).
      <button
        type="button"
        role="checkbox"
        aria-checked={isSelected(org)}
        aria-label={`Select ${orgDisplayName(org, nameById)}`}
        className={`care-org-select${isSelected(org) ? " is-selected" : ""}`}
        disabled={busyOrgId === org.org_id || submitting}
        onClick={() => toggleSelected(org)}
      >
        {busyOrgId === org.org_id ? (
          <IonSpinner name="dots" />
        ) : (
          <IonIcon icon={isSelected(org) ? checkmarkCircle : ellipseOutline} />
        )}
      </button>
    ) : org.shareable ? (
      <IonButton
        className="care-org-btn care-org-btn-primary"
        disabled={busyOrgId === org.org_id || submitting}
        onClick={() => void doShare([org])}
      >
        {busyOrgId === org.org_id ? <IonSpinner name="dots" /> : "Share"}
      </IonButton>
    ) : (
      // Not shareable right now — e.g. single_org and the patient already shares with another
      // location. They can switch by revoking the current share first.
      <IonButton className="care-org-btn care-org-btn-outline" fill="outline" disabled>
        Not available
      </IonButton>
    );

  // ── Autocomplete dropdown: a flat list of matching locations ────────────────────────────────
  if (showAsDropdown) {
    const matches = filterOrgs(flattenLocations(orgs), search, nameById);
    return (
      <div className="care-org-inline">
        {blocked ? (
          <p className="care-org-row-meta">
            Sharing with organizations isn&apos;t available on your account.
          </p>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: 12 }}>
            <IonSpinner name="crescent" />
          </div>
        ) : matches.length === 0 ? (
          <p className="care-org-row-meta">No matching locations.</p>
        ) : (
          <div className="org-autocomplete-list">
            {matches.map((loc) => (
              <OrgRow
                key={loc.org_id}
                org={loc}
                parentName={parentNameOf(loc)}
                trailing={trailingFor(loc)}
              />
            ))}
          </div>
        )}
        <UnshareOrgConfirm
          org={unshareOrg}
          displayName={unshareOrg ? orgDisplayName(unshareOrg, nameById) : ""}
          onConfirm={doUnshare}
          onCancel={() => setUnshareOrg(null)}
        />
      </div>
    );
  }

  // ── Card / accordion view ───────────────────────────────────────────────────────────────────
  const parents = filterOrgTree(orgs, search, nameById);
  const visibleParents = expanded ? parents : parents.slice(0, INITIAL_PARENTS);
  const hiddenCount = parents.length - visibleParents.length;

  // A parent is open when the user expanded it, or whenever a search is active (so matching
  // locations are visible without an extra tap).
  const isOpen = (parentId: number) => searching || openParents.has(parentId);
  const toggleParent = (parentId: number) =>
    setOpenParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });

  // Before the patient types there is nothing to list (F-468): drop the card chrome too, so the
  // popup shows no empty white box under the search.
  const bare = !autocomplete && !searching && !blocked && !(showHeader && (title || subtitle));

  return (
    <div className={autocomplete ? "care-org-inline" : bare ? undefined : "care-org-card"}>
      {showHeader && title && (
        <div className="care-org-section-label">
          <IonIcon icon={businessOutline} color="primary" />
          <span>{title}</span>
        </div>
      )}
      {showHeader && subtitle && <p className="care-org-sub">{subtitle}</p>}

      {blocked ? (
        <p className="care-org-row-meta">
          Sharing with organizations isn&apos;t available on your account.
        </p>
      ) : !searching ? null : loading ? (
        // Nothing is listed until the patient types (F-468).
        <div style={{ textAlign: "center", padding: 12 }}>
          <IonSpinner name="crescent" />
        </div>
      ) : parents.length === 0 ? (
        <p className="care-org-row-meta">
          {searching ? "No matching organizations." : "No organizations available."}
        </p>
      ) : (
        <>
          {visibleParents.map((parent) => {
            const locations = parent.locations ?? [];
            const open = isOpen(parent.org_id);
            return (
              <div key={parent.org_id} className="care-org-accordion-group">
                <OrgAccordionHeader
                  org={parent}
                  locationCount={locations.length}
                  expanded={open}
                  onToggle={() => toggleParent(parent.org_id)}
                />
                {open && (
                  <div className="care-org-locations">
                    {locations.length === 0 ? (
                      <p className="care-org-row-meta care-org-locations-empty">
                        No locations available.
                      </p>
                    ) : (
                      locations.map((loc) => (
                        // Nested under the parent header, so show just the location name.
                        <OrgRow key={loc.org_id} org={loc} trailing={trailingFor(loc)} />
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {hiddenCount > 0 && (
            <button
              className="care-org-seeall"
              type="button"
              onClick={() => setExpanded(true)}
            >
              Show all ({parents.length})
            </button>
          )}
        </>
      )}

      {selected.length > 0 && !blocked && (
        // Sticky so the action stays in reach while the results scroll under it.
        <div className="care-org-share-bar">
          <IonButton
            className="care-org-btn care-org-btn-primary"
            expand="block"
            disabled={submitting}
            onClick={() => void doShare(selected)}
          >
            {submitting ? (
              <IonSpinner name="dots" />
            ) : selected.length === 1 ? (
              "Share"
            ) : (
              `Share with ${selected.length} locations`
            )}
          </IonButton>
        </div>
      )}

      <UnshareOrgConfirm
        org={unshareOrg}
        displayName={unshareOrg ? orgDisplayName(unshareOrg, nameById) : ""}
        onConfirm={doUnshare}
        onCancel={() => setUnshareOrg(null)}
      />
    </div>
  );
};

export default OrgShareCard;
